import React from "react"
import styled from "styled-components"
import { humanReadableSize } from "../../../../Computation/utilFunctions"


// Minimal storage indicator for the Export Tracker modal / per-patient tab
// headers. A thin horizontal bar + compact "used / total" text — sized to sit
// quietly in a header row alongside the tabs, not a feature widget. Renders:
//   - loading       (stats === null)        → shimmer bar skeleton
//   - no-total      (disk_total_bytes null) → "X used" text, no bar
//   - normal/full   → label · bar · "used / total" (+ red note when full)
//
// Bar color tiers match the status-pill palette:
//   < 75% blue · 75–90% orange · ≥ 90% red.


const Row = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: var(--text-secondary-color);
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
`

const Label = styled.span`
    font-weight: 600;
    color: var(--text-secondary-color);
`

const Track = styled.span`
    display: inline-flex;
    width: 96px;
    height: 6px;
    background: var(--color-hover-neutral);
    border-radius: 999px;
    overflow: hidden;
`

const Fill = styled.span.attrs(props => ({
    style: {
        width: `${Math.min(Math.max(props.$pct, props.$pct > 0 ? 2 : 0), 100)}%`,
        background: props.$color,
    },
}))`
    display: block;
    flex: 0 0 auto;
    height: 100%;
    transition: width 0.5s ease, background 0.25s ease;
`

// Space promised to queued exports that have not written anything yet. Drawn as a
// separate, translucent segment: without it the bar shows free space the API will
// already refuse to use, which reads as a bug rather than as a queue.
const Committed = styled.span.attrs(props => ({
    style: { width: `${Math.min(Math.max(props.$pct, props.$pct > 0 ? 2 : 0), 100)}%` },
}))`
    display: block;
    flex: 0 0 auto;
    height: 100%;
    background: repeating-linear-gradient(
        135deg,
        var(--color-hover-neutral) 0 3px,
        rgba(120, 120, 120, 0.55) 3px 6px
    );
    transition: width 0.5s ease;
`

const Values = styled.span`
    color: var(--text-primary-color);
    font-weight: 600;
`

const Muted = styled.span`
    color: var(--text-secondary-color);
    font-weight: 400;
`

const FullNote = styled.span`
    color: #F44336;
    font-weight: 600;
`

const Skeleton = styled.span`
    display: inline-block;
    width: 180px;
    height: 6px;
    border-radius: 999px;
    background: linear-gradient(
        90deg,
        rgba(33, 150, 243, 0.08) 0%,
        rgba(33, 150, 243, 0.22) 50%,
        rgba(33, 150, 243, 0.08) 100%
    );
    background-size: 200% 100%;
    animation: storage-bar-shimmer 1.4s ease-in-out infinite;

    @keyframes storage-bar-shimmer {
        0%   { background-position: 200% 0; }
        100% { background-position: -200% 0; }
    }
`

function fillColor(pct) {
    if (pct >= 90) return "#F44336"
    if (pct >= 75) return "#FF9800"
    return "var(--color-accent-interactive)"
}


export default function ExportsStorageMeter({ stats }) {
    if (!stats) {
        return <Row><Skeleton aria-label="Loading export storage" /></Row>
    }

    const {
        total_bytes,
        disk_total_bytes,
        quota_source,
        committed_bytes = 0,
        reserved_bytes = 0,
    } = stats

    // No known total → can't draw a bar. Compact text fallback.
    if (!disk_total_bytes) {
        return <Row><Values>{humanReadableSize(total_bytes)}</Values><Muted>used</Muted></Row>
    }

    // disk_total_bytes is the USABLE capacity: the server has already taken the
    // partition reserve off it. So the bar filling up means exports will be
    // refused, which is the whole point — the number shown has to be the number
    // that governs, or "8 GB free" sits next to a refusal and looks broken.
    const usedPct = Math.min(100, (total_bytes / disk_total_bytes) * 100)
    const committedPct = Math.min(100 - usedPct, (committed_bytes / disk_total_bytes) * 100)
    const color = fillColor(usedPct + committedPct)

    // Full once committed work is counted, not just what has landed. A queue that
    // has already claimed the remaining space is full for every practical purpose.
    const isFull = (total_bytes + committed_bytes) >= disk_total_bytes

    const limitNote = {
        quota: "Configured export storage limit for this deployment.",
        reserve: "Limited by free space on the export disk, keeping some back for the server.",
        filesystem: "Limited by free space on the export disk, shared with other patient data.",
    }[quota_source] || "Export storage."

    const tooltip = [
        limitNote,
        `${humanReadableSize(total_bytes)} written by finished exports.`,
        committed_bytes > 0
            ? `${humanReadableSize(committed_bytes)} reserved by exports still queued or running.`
            : null,
        // Only when the reserve is what binds. Under a quota it is not the limit
        // that ran out, so naming it just raises a question with no useful answer.
        reserved_bytes > 0 && quota_source === "reserve"
            ? `${humanReadableSize(reserved_bytes)} held back so exports cannot fill the disk.`
            : null,
    ].filter(Boolean).join(" ")

    return (
        <Row title={tooltip}>
            <Label>Storage</Label>
            <Track>
                <Fill $pct={usedPct} $color={color} />
                <Committed $pct={committedPct} />
            </Track>
            <span>
                <Values>{humanReadableSize(total_bytes)}</Values>
                {committed_bytes > 0 && (
                    <Muted> + {humanReadableSize(committed_bytes)} queued</Muted>
                )}
                <Muted> / {humanReadableSize(disk_total_bytes)}</Muted>
            </span>
            {isFull && <FullNote>Full. Delete exports to free space.</FullNote>}
        </Row>
    )
}
